import { View } from "react-native";
import React, { useState, useEffect } from "react";
import { useLocalSearchParams, useRouter, useNavigation } from "expo-router";
import { useAuth } from "@components/auth/useAuth";

import { CustomAlert } from "@/components/ThemedAlert";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMyContacts, editContact, unlinkContact, deleteContact } from "@/services/firebase/user-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { getMyGroups } from "@/services/firebase/group-functions";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { ContactModel, GroupModel, ShareModel } from "common247";
import ThemedScreen from "@components/ThemedScreen";
import { ThemedKebabMenu, IKebabActionItem } from "@/components/ThemedKebabMenu";
import { ContactEditor } from "@/components/contacts/ContactEditor";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { navigateBackOrToList } from "@/utils/navigation";

import { useAppDispatch } from "@/services/store/hooks";
import { updateContact } from "@/services/store/features/contactsSlice";
import { entitySync } from "@/services/sync/entity-sync";

export default function ContactDetailsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const services = useFirebase();
  const { user } = useAuth();
  const router = useRouter();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  const [contact, setContact] = useState<ContactModel | null>(null);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = async () => {
    if (!services || !id) return;
    setLoading(true);
    try {
      const [contactsRes, groupsRes, sharesRes] = await Promise.all([
        getMyContacts(services, { includeInactive: true }),
        getMyGroups(services, {}),
        getSharesForSource(services, { sourceId: id, sourceType: "contact" }),
      ]);

      if (contactsRes.status) {
        const found = contactsRes.contacts?.find((c) => c.id === id);
        if (found) {
          setContact(found);
        } else {
          CustomAlert.alert("Error", "Contact not found.");
          navigateBackOrToList(router, "/(tabs)/my-contacts", navigation);
        }
      }

      if (groupsRes.status) {
        setGroups(groupsRes.groups || []);
      }

      if (sharesRes.status) {
        setShares(sharesRes.shares);
      }
    } catch (error) {
      console.error("Error fetching contact details:", error);
      CustomAlert.alert("Error", "Failed to load contact details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [services, id]);

  const handleSave = async (formData: any) => {
    if (!services || !id) return;
    setIsSaving(true);
    try {
      const res = await editContact(services, {
        contactId: id,
        ...formData,
      });
      if (res.status) {
        const updatedContact: ContactModel = {
          ...(contact || ({} as ContactModel)),
          ...formData,
          id,
        };
        // Auto-save updates in place — do not close the edited item
        setContact((prev) => (prev ? { ...prev, ...formData } : updatedContact));
        dispatch(updateContact(updatedContact));
        entitySync.emit({
          type: "contact",
          action: "update",
          id,
          data: updatedContact,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to update contact");
      }
    } catch (e) {
      CustomAlert.alert("Error", "Unexpected error while saving");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!services || !id) return;
    if (contact && user?.uid && contact.userId === user.uid && contact.ownerId === user.uid) {
      CustomAlert.alert("Error", "The primary account contact cannot be unlinked.");
      return;
    }
    setIsUnlinking(true);
    try {
      const res = await unlinkContact(services, { contactId: id });
      if (res.status) {
        setContact((prev) => (prev ? { ...prev, userId: undefined, email: "" } : prev));
        CustomAlert.alert("Success", "Contact unlinked successfully. Email address cleared.");
      } else {
        CustomAlert.alert("Error", res.error || "Failed to unlink contact");
      }
    } catch (e: any) {
      CustomAlert.alert("Error", e?.message || "Unexpected error while unlinking contact");
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleShare = async (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    if (!services || !id) return;
    try {
      const res = await share(services, {
        sourceId: id,
        sourceType: "contact",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        const updatedShares = await getSharesForSource(services, { sourceId: id, sourceType: "contact" });
        if (updatedShares.status) setShares(updatedShares.shares);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to share.");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !id) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        const updatedShares = await getSharesForSource(services, { sourceId: id, sourceType: "contact" });
        if (updatedShares.status) setShares(updatedShares.shares);
      }
    } catch (error) {
      console.error("Error unsharing:", error);
    }
  };

  const handleDelete = async () => {
    if (!services || !id) return;
    await confirmEntityDeletion({
      services,
      entityType: "contact",
      entityId: id,
      entityName: contact?.displayName,
      onProceedDelete: async (cascadeUnlink) => {
        setIsDeleting(true);
        try {
          const res = await deleteContact(services, { contactId: id, cascadeUnlink });
          if (!res.status) {
            setIsDeleting(false);
          }
          return res;
        } catch (error) {
          setIsDeleting(false);
          throw error;
        }
      },
      onSuccess: () => {
        navigateBackOrToList(router, "/(tabs)/my-contacts", navigation);
      },
      onError: () => {
        setIsDeleting(false);
      },
    });
  };

  const userRole = contact?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";
  const isViewer = userRole === "viewer";

  const kebabActions: IKebabActionItem[] = [];
  if (contact?.userId && canEdit) {
    kebabActions.push({
      id: "unlink",
      label: "Unlink Contact",
      icon: "link-outline",
      onPress: handleUnlink,
      disabled: isUnlinking,
    });
  }
  if (contact && canEdit) {
    kebabActions.push({
      id: "delete",
      label: "Delete Contact",
      icon: "trash-outline",
      destructive: true,
      onPress: handleDelete,
      disabled: isDeleting,
    });
  }

  return (
    <ThemedScreen
      title={contact ? (isOwner ? `Edit: ${contact.displayName}` : `View: ${contact.displayName}`) : "Contact Details"}
      loading={loading}
      rightComponent={kebabActions.length > 0 ? <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Contact options" /> : undefined}
    >
      <View style={{ flex: 1 }}>
        <ThemedProgressBar loading={loading} />
        {!loading && contact && (
          <ContactEditor
            contact={contact}
            allGroups={groups}
            shares={shares}
            isLoadingShares={false}
            currentUserId={user?.uid}
            onSave={handleSave}
            onShare={handleShare}
            onUnshare={handleUnshare}
            onUnlink={handleUnlink}
            onCancel={() => navigateBackOrToList(router, "/(tabs)/my-contacts", navigation)}
            isSaving={isSaving}
            isUnlinking={isUnlinking}
          />
        )}
      </View>
    </ThemedScreen>
  );
}
